// Helpers for rendering URLs that come from API / user / creator data.
//
// Never put such a value straight into href / src / window.open: a value like
// "javascript:...", " JaVaScRiPt:...", "data:..." or "vbscript:..." would run
// script (or load attacker content) in the panel's origin, and "//evil.com"
// is a protocol-relative link to another host.

// ASCII control chars. Browsers silently drop tab / CR / LF anywhere in a URL,
// so "/\t/evil.com" would become "//evil.com" and "java\tscript:" a scheme.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\x00-\x1f\x7f]/;

const normalize = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") return "";
  return value.trim();
};

/**
 * Returns the URL string only if it is an absolute http:// or https:// URL,
 * otherwise null. Relative paths, protocol-relative ("//host"), javascript:,
 * data:, vbscript:, file:, mailto:, tel: etc. all return null.
 */
export const safeHttpUrl = (url) => {
  const raw = normalize(url);
  if (!raw) return null;
  if (CONTROL_CHARS.test(raw)) return null;
  // Literal prefix check: "java\tscript:", " JaVaScRiPt:", data:, vbscript:,
  // "//host" and relative values all fail here.
  if (!/^https?:\/\//i.test(raw)) return null;
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    if (!parsed.hostname) return null;
    return raw;
  } catch (_) {
    return null;
  }
};

/**
 * True for an in-app relative path such as "/admin/movie?id=1".
 * Rejects "//host", "/\host" (browsers treat "\" as "/"), and anything with
 * a scheme or control/space characters.
 */
export const isSafeInternalPath = (p) => {
  const raw = normalize(p);
  if (!raw) return false;
  if (CONTROL_CHARS.test(raw)) return false;
  if (raw[0] !== "/") return false;
  if (raw[1] === "/" || raw[1] === "\\") return false;
  return true;
};

/**
 * Safe value for an <a href>: an absolute http(s) URL or an in-app path.
 * Returns null when the value must not be used as a link.
 */
export const safeLinkHref = (url) => {
  const http = safeHttpUrl(url);
  if (http) return http;
  return isSafeInternalPath(url) ? normalize(url) : null;
};

/**
 * Safe value for media src attributes (<img>, <video>, <iframe> of uploaded
 * files): http(s) URLs, in-app paths, and local blob: object URLs created by
 * the panel itself (URL.createObjectURL previews).
 */
export const safeMediaSrc = (url) => {
  const link = safeLinkHref(url);
  if (link) return link;
  const raw = normalize(url);
  if (/^blob:https?:\/\//i.test(raw) && !CONTROL_CHARS.test(raw)) {
    return raw;
  }
  return null;
};

/** mailto: link only for a plausible single email address, else null. */
export const safeMailtoHref = (email) => {
  const raw = normalize(email);
  if (!raw || !/^[^\s@<>"'`/\\:;,]+@[^\s@<>"'`/\\:;,]+\.[^\s@<>"'`/\\:;,]+$/.test(raw)) {
    return null;
  }
  return `mailto:${raw}`;
};

/** tel: link only for a plausible phone number, else null. */
export const safeTelHref = (phone) => {
  const raw = normalize(phone);
  if (!raw || !/^\+?[0-9 ()\-.]{3,25}$/.test(raw)) return null;
  return `tel:${raw.replace(/[^0-9+]/g, "")}`;
};

/**
 * window.open only for safe http(s) URLs (or in-app paths), always with
 * noopener,noreferrer. Returns true if a window was opened.
 */
export const openSafeUrl = (url, target = "_blank") => {
  const href = safeLinkHref(url);
  if (!href) return false;
  window.open(href, target, "noopener,noreferrer");
  return true;
};
